/** Sample workspaces are selectors, never login credentials. */
import { z } from 'zod';

import { EXAM_LEVELS, validExamLevelsForOrgLevel } from './examLevels.js';
import { ORG_LEVEL_DISPLAY_LABELS, ORG_LEVELS } from './orgLevels.js';

export const SandboxViewSchema = z.enum(['teacher', 'student']);
export type SandboxView = z.infer<typeof SandboxViewSchema>;
export const SandboxCohortSchema = z
  .object({
    orgLevel: z.enum(ORG_LEVELS),
    examLevel: z.enum(EXAM_LEVELS),
    mathSubject: z.enum(['E-Math', 'A-Math']),
  })
  .strict()
  .refine(
    c => validExamLevelsForOrgLevel(c.orgLevel).includes(c.examLevel),
    'Invalid level and stream'
  )
  .refine(
    c =>
      c.mathSubject === 'E-Math' ||
      (['secondary_3', 'secondary_4'].includes(c.orgLevel) &&
        ['G2', 'G3'].includes(c.examLevel)),
    'A-Math requires Sec 3 or Sec 4 G2/G3'
  );
export type SandboxCohort = z.infer<typeof SandboxCohortSchema>;
export const DEFAULT_SANDBOX_COHORT: SandboxCohort = {
  orgLevel: 'secondary_3',
  examLevel: 'G3',
  mathSubject: 'E-Math',
};
export const sandboxCohortKey = (c: SandboxCohort): string =>
  `${c.orgLevel}:${c.examLevel}:${c.mathSubject}`;
export const sandboxCohortLabel = (c: SandboxCohort): string =>
  `${ORG_LEVEL_DISPLAY_LABELS[c.orgLevel]} · ${c.examLevel} · ${c.orgLevel.startsWith('primary_') ? 'Maths' : c.mathSubject}`;
export const SandboxSelectionSchema = z
  .object({
    workspaceId: z.string().regex(/^[a-f\d]{24}$/i),
    view: SandboxViewSchema,
  })
  .strict();
export type SandboxSelection = z.infer<typeof SandboxSelectionSchema>;
export const SandboxCommandSchema = z
  .object({
    view: SandboxViewSchema,
    cohort: SandboxCohortSchema.optional(),
    workspaceId: z
      .string()
      .regex(/^[a-f\d]{24}$/i)
      .optional(),
    fresh: z.boolean().optional(),
    requestKey: z.string().uuid(),
  })
  .strict()
  .refine(
    c => !(c.cohort && c.workspaceId),
    'Choose a cohort or a workspace, not both'
  )
  .refine(
    c => !(c.fresh && c.workspaceId),
    'A fresh workspace cannot name an existing workspace'
  );
export type SandboxCommand = z.infer<typeof SandboxCommandSchema>;
export interface SandboxWorkspaceSummary {
  id: string;
  cohort: SandboxCohort;
  createdAt: string;
}
export interface SandboxViewState {
  available: boolean;
  active: boolean;
  selection: SandboxSelection | null;
  workspace: SandboxWorkspaceSummary | null;
  actor: { displayName: string | null };
  effectiveUserId?: string;
}
